"use client";

import React, { useEffect, useState, useCallback } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { LayoutState } from '@/types';
import { getAvatarColor, getInitials } from '@/lib/utils';
import { X } from 'lucide-react';
import styles from './MessageToast.module.css';

interface ToastItem {
    id: string;
    senderName: string;
    message: string;
    groupID: string;
}

export default function MessageToast() {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const { selectRoom, rooms, setLayoutState } = useChatContext();

    const dismiss = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const handleClick = useCallback((toast: ToastItem) => {
        dismiss(toast.id);
        const room = rooms.find(r => r.groupID === toast.groupID);
        if (room) {
            setLayoutState(LayoutState.Minimize);
            selectRoom(room);
        }
    }, [rooms, selectRoom, setLayoutState, dismiss]);

    useEffect(() => {
        const handleNotification = (event: Event) => {
            const { senderName, message, groupID } = (event as CustomEvent).detail;
            const id = `${Date.now()}-${Math.random()}`;
            setToasts(prev => [{ id, senderName, message, groupID }, ...prev].slice(0, 3));
            setTimeout(() => dismiss(id), 4000);
        };

        window.addEventListener('jfc-chat:in-app-notification', handleNotification);
        return () => window.removeEventListener('jfc-chat:in-app-notification', handleNotification);
    }, [dismiss]);

    if (toasts.length === 0) return null;

    return (
        <div className={styles.container}>
            {toasts.map(toast => (
                <div key={toast.id} className={styles.toast} onClick={() => handleClick(toast)}>
                    <div
                        className={styles.avatar}
                        style={{ background: getAvatarColor(toast.senderName) }}
                    >
                        {getInitials(toast.senderName)}
                    </div>
                    <div className={styles.content}>
                        <span className={styles.name}>{toast.senderName}</span>
                        <span className={styles.message}>{toast.message}</span>
                    </div>
                    <button
                        className={styles.close}
                        onClick={e => { e.stopPropagation(); dismiss(toast.id); }}
                    >
                        <X size={12} />
                    </button>
                </div>
            ))}
        </div>
    );
}
